import React, {
  forwardRef,
  Ref,
  useMemo,
  FocusEvent,
  useCallback,
} from "react";
import {
  DayPicker,
  DayPickerProps,
  PropsSingle,
  NavProps,
  Matcher,
  ClassNames,
} from "react-day-picker";
import classNames from "classnames";

import moment from "moment";
import Text from "../../type/Text/Text";
import Input, { InputStatus } from "../Input/Input";
import { RoundedVariantProps } from "../Input/inputVariants";

import DropdownWrapper from "../../../utils/DropdownWrapper/DropdownWrapper";
import Icon, { IconNames } from "../../icons/Icon/Icon";
import Alerts, { AlertEnum } from "../../dialogs/Alerts/Alerts";
import useSubscription from "@src/global_functions/hooks/useSubscription";
import Button from "../../buttons/Button/Button";

import styles from "./DatePicker.module.scss";
import { Include } from "@src/straps/colors";
import { noop } from "lodash";

// eslint-disable-next-line react/display-name
const NullMonthCaption = (): JSX.Element => null as unknown as JSX.Element;

type DatePickerProps = {
  readonly value?: string | null;
  readonly label?: string;
  readonly format?: string | string[];
  readonly className?: string | null;
  readonly disabledDates?: Matcher[];
  readonly style?: Record<any, any>;
  readonly handleDateChange?: (arg1: string | null) => void;
  readonly defaultMonth?: Date;
  readonly enableFutureDates?: boolean;
  readonly hideTodayButton?: boolean;

  // DayPickerProps

  readonly modifiers?: DayPickerProps["modifiers"];
  readonly modifiersClassNames?: DayPickerProps["modifiersClassNames"];

  // input props

  readonly status?: InputStatus;
  readonly supportText?: string;
  readonly validation?: string;
  readonly extendContainerRefs?: React.RefObject<HTMLElement>[];
  readonly extraDropdownRef?: React.RefObject<HTMLDivElement>;
  readonly backgroundColor?: RoundedVariantProps["backgroundColor"];
  readonly placeholder?: string;
  readonly min?: string;
};

/**
 * @param format provide date format if not "YYYY-MM-DD or "MMM D, YYYY"
 */
export default function DatePickerInput({
  name,
  disabled,
  value,
  label,
  status,
  supportText,
  validation,
  extendContainerRefs,
  extraDropdownRef,
  backgroundColor,
  dataTestId,
  placeholder,
  min = "1000-01-01", // min ensures date is valid and has a year with 4 digits
  ...datePickerProps
}: Omit<DatePickerProps, "onCancel"> & {
  readonly name: string;
  readonly dataTestId: string;
  readonly disabled?: boolean;
}) {
  const [isActive, setIsActive] = React.useState(false);
  const [closeSubscription, closeEvent] = useSubscription();
  const [internalValue, setInternalValue] = React.useState<
    string | null | undefined
  >();
  const [error, setError] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLInputElement>(null);
  const _extraDropdownRef = React.useRef<HTMLDivElement>(null);
  const allRefs = React.useMemo(
    () =>
      [
        containerRef,
        ...(extendContainerRefs ?? []),
      ] as React.RefObject<HTMLElement>[],
    [containerRef, extendContainerRefs]
  );

  React.useEffect(() => {
    setInternalValue(value);
  }, [value]);

  React.useEffect(() => {
    // close dropdown on date selection.
    closeEvent();
  }, [value, closeEvent]);

  const dateInputVal = internalValue
    ? moment(internalValue, datePickerProps.format).format("YYYY-MM-DD")
    : "";
  const maxInputVal = datePickerProps.enableFutureDates
    ? "9999-12-31"
    : moment().format("YYYY-MM-DD");

  // update value in input on every change, but only call on change param if input value is a valid date
  function handleDateInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInternalValue(e.target.value);

    if (e.target.validity.valid) {
      setError(null);
      datePickerProps.handleDateChange?.(e.target.value);
    } else if (
      !datePickerProps.enableFutureDates &&
      e.target.validity.rangeOverflow
    ) {
      setError(
        "Future dates are not allowed for this field. Please select a date in the past."
      );
    }
  }

  function handleDateSelect(date: string | null) {
    datePickerProps.handleDateChange?.(date);
    setError(null);
  }

  React.useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (
        ![...allRefs, _extraDropdownRef, extraDropdownRef].some((ref) =>
          ref?.current?.contains(e.target as Node)
        )
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [allRefs, extraDropdownRef, _extraDropdownRef]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <DropdownWrapper
        open={typeof error === "string"}
        onFocus={() => {
          setIsActive(true);
        }}
        onBlur={(e) => {
          // if local value is invalid when user clicks out, reset to last valid value
          if (
            !internalValue ||
            !(e as FocusEvent<HTMLInputElement>).target.validity.valid
          ) {
            datePickerProps.handleDateChange?.(null);
          }
        }}
        as={
          <Input
            dataTestId={dataTestId}
            name={name}
            label={label}
            disabled={disabled}
            placeholder={placeholder}
            value={dateInputVal ?? ""}
            // Set type to text so placeholder is shown when value is empty
            type={!internalValue && !isActive && placeholder ? "text" : "date"}
            variant="rounded"
            onChange={handleDateInputChange}
            status={status}
            supportText={supportText}
            validation={validation}
            min={min}
            max={maxInputVal}
            backgroundColor={backgroundColor}
          />
        }
      >
        {<Alerts status={AlertEnum.Error} message={error} />}
      </DropdownWrapper>
      <DropdownWrapper
        closeSubscription={closeSubscription}
        extraRefs={allRefs}
        extraDropdownRef={extraDropdownRef ?? _extraDropdownRef}
        orientRef={containerRef}
        as={
          <Icon
            name="date-picker"
            className={classNames(
              "absolute right-4 cursor-pointer text-straps-secondary transition-colors hover:text-straps-primary",
              {
                "top-2": !label,
                "bottom-2": !!label,
              }
            )}
            data-testid={`${dataTestId}-open-picker`}
          />
        }
      >
        <DatePicker
          value={internalValue}
          {...datePickerProps}
          handleDateChange={handleDateSelect}
        />
      </DropdownWrapper>
    </div>
  );
}

export const MonthNavButton = ({
  icon,
  onClick,
}: Readonly<{
  icon: Include<IconNames, "caret-left" | "caret-right">;
  onClick: NavProps["onPreviousClick"] | NavProps["onNextClick"];
}>) => (
  <Button
    dataTestId={`date-picker-month-nav-${icon}`}
    noFrameVariant="icon"
    icon={icon}
    color="secondary"
    className="!flex !h-10 items-center px-2 !text-straps-tertiary duration-100 hover:!text-white"
    onClick={onClick}
  />
);

const DatePickerNavBar = (props: NavProps) => {
  const month = moment(props.previousMonth).add(1, "month").format("MMMM yyyy");
  return (
    <div className="flex items-center justify-between bg-straps-primary-hover">
      <MonthNavButton onClick={props.onPreviousClick} icon="caret-left" />
      <Text variant="sb_t-14-500" color="white">
        {month}
      </Text>
      <MonthNavButton onClick={props.onNextClick} icon="caret-right" />
    </div>
  );
};

export const TodayButton = ({
  onTodayClick,
}: Readonly<{ onTodayClick?: () => void }>) => {
  return (
    <div className="flex h-10 items-center justify-center border-t border-straps-accent-3/50 bg-white">
      <button
        className="cursor-pointer border-0 bg-transparent text-xs text-[#416ce9]"
        onClick={onTodayClick}
      >
        Today
      </button>
    </div>
  );
};

export const sharedClassNames = {
  root: "inline-block",
  month: "flex flex-nowrap justify-center",
  month_grid:
    "my-[9px] mx-[25px] table border-spacing-0 border-collapse select-none",
  weekdays: "border-b border-straps-accent-3/50 flex",
  weekday:
    "h-8 w-8 flex items-center justify-center text-xs text-[#9a9eb2] text-center font-medium",
  weeks: "flex flex-col mt-0.5 shadow-invisible",
  week: "flex",
  day_button: "rdp-day_button w-8 h-8 -my-0.5",
};

export const DatePicker = forwardRef(function DatePicker(
  {
    value,
    format = ["YYYY-MM-DD", "MMM D, YYYY"],
    className,
    handleDateChange,
    disabledDates,
    defaultMonth,
    enableFutureDates,
    modifiers,
    modifiersClassNames,
    hideTodayButton = false,
    ...props
  }: DatePickerProps,
  ref: Ref<HTMLDivElement>
) {
  const disabled = useMemo(() => {
    const result: Matcher[] = enableFutureDates
      ? []
      : [{ after: moment().toDate() }];

    if (disabledDates) result.push(...disabledDates);
    return result;
  }, [disabledDates, enableFutureDates]);
  // moment() parses dates to local timezone unless offset is included
  // https://momentjs.com/guides/#/parsing/local-utc-zone/
  const dayPickerProps = useMemo(
    () =>
      ({
        mode: "single",
        selected: value ? moment(value, format).toDate() : undefined,
        numberOfMonths: 1,
        components: { Nav: DatePickerNavBar, MonthCaption: NullMonthCaption },
        showOutsideDays: true,
        disabled,
        defaultMonth:
          (value ? moment(value, format).toDate() : defaultMonth) ??
          moment().toDate(),
        //  https://github.com/gpbl/react-day-picker/issues/2690
        onSelect: noop,
        onDayClick: (day, _modifiers) => {
          // day is a Date in local timezone
          const formattedDate = moment(day).format("YYYY-MM-DD");
          if (_modifiers && !_modifiers.disabled) {
            handleDateChange?.(formattedDate);
          }
        },
        modifiers,
        modifiersClassNames,
      } satisfies Omit<DayPickerProps, "selected" | "mode"> &
        Pick<PropsSingle, "mode" | "selected" | "onSelect">),
    [
      defaultMonth,
      disabled,
      format,
      handleDateChange,
      value,
      modifiers,
      modifiersClassNames,
    ]
  );

  const _classNames = useMemo(
    () =>
      ({
        ...sharedClassNames,
        months: "relative z-[1000] bg-white select-none focus:outline-none",
        day: classNames(
          "text-sm flex h-8 w-8 my-0.5 z-0 relative bg-transparent transition-colors duration-[250ms] cursor-pointer items-center justify-center",
          styles.day
        ),
        // built-in day modifiers
        selected: styles.selected,
        outside: styles.outside,
        disabled: styles.disabled,
      } satisfies Partial<ClassNames>),
    []
  );

  const onTodayClick = useCallback(() => {
    handleDateChange?.(moment().format("YYYY-MM-DD"));
  }, [handleDateChange]);

  return (
    <div
      data-testid="date-picker-container"
      className={classNames(className, "relative w-fit")}
      ref={ref}
      {...props}
    >
      <DayPicker {...dayPickerProps} classNames={_classNames} />
      {!hideTodayButton && <TodayButton onTodayClick={onTodayClick} />}
    </div>
  );
});
